import useTranslation from '../../hooks/useTranslation';
import FormulationCurrentSummary from '../FormulationCurrentSummary';
import FormulationDocumentStatements from '../FormulationDocumentStatements';
import FormulationEvidenceFields from '../FormulationEvidenceFields';

import { VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import Banner from 'components/Banner';
import Skeleton from 'components/loaders/Skeleton';

import { LOADING_SKELETON_KEYS, TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const FormulationReviewContent: FunctionComponent<Props> = ({
    actionError,
    canVerify,
    className = '',
    documents,
    evidence,
    hasContradiction,
    isBusy,
    labelMixingNote,
    onEvidenceChange,
    onLabelMixingNoteChange,
    onSelectStatement,
    reviewState,
    selectedStatementKey,
    suggestedCodes,
    testId = TEST_ID
}) => {
    const t = useTranslation();
    const rootClass = `${styles.formulationReviewContent} ${className}`;

    if (reviewState.kind === 'loading') {
        return (
            <VerticalContainer className={rootClass} testId={testId}>
                <Wrapper className={styles.loadingText}>{t.LOADING}</Wrapper>

                {LOADING_SKELETON_KEYS.map(key => (
                    <Skeleton className={styles.skeleton} key={key} />
                ))}
            </VerticalContainer>
        );
    }

    if (reviewState.kind === 'error') {
        return (
            <VerticalContainer className={rootClass} testId={testId}>
                <Banner text={reviewState.message} variant='warn' />
            </VerticalContainer>
        );
    }

    return (
        <VerticalContainer className={rootClass} testId={testId}>
            <FormulationCurrentSummary current={reviewState.review.current} />

            {!canVerify && <Banner text={t.READ_ONLY} variant='info' />}

            {hasContradiction && (
                <Banner text={t.contradiction(suggestedCodes.join(', '))} variant='warn' />
            )}

            {documents.map(document => (
                <FormulationDocumentStatements
                    document={document}
                    key={document.key}
                    onSelectStatement={onSelectStatement}
                    selectedStatementKey={selectedStatementKey}
                />
            ))}

            {canVerify && selectedStatementKey && (
                <FormulationEvidenceFields
                    evidence={evidence}
                    isDisabled={isBusy}
                    labelMixingNote={labelMixingNote}
                    onEvidenceChange={onEvidenceChange}
                    onLabelMixingNoteChange={onLabelMixingNoteChange}
                />
            )}

            {actionError && <Banner text={actionError} variant='warn' />}
        </VerticalContainer>
    );
};

export default FormulationReviewContent;
